import React from "react";
import { useI18n } from "../../i18n";
import { ArrowRight } from "lucide-react";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1624583338957-4d155ca886dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwY2FmZSUyMGludGVyaW9yfGVufDB8fHx8MTc3OTAwNDg0MXww&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const { t } = useI18n();

  return (
    <section id="top" className="relative pt-32 md:pt-36 pb-16 md:pb-24 overflow-hidden">
      {/* Soft background wash */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-[520px] -z-10"
        style={{
          background:
            "radial-gradient(1200px 480px at 20% 20%, rgba(200,107,83,0.10), transparent 60%), radial-gradient(900px 400px at 80% 0%, rgba(26,54,45,0.07), transparent 60%)",
        }}
      />
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="reveal" data-delay="0">
            <p className="eyebrow" data-testid="hero-eyebrow">
              {t("hero.eyebrow")}
            </p>
          </div>
          <h1 className="display-h1 mt-5 text-brand-ink reveal" data-delay="1" data-testid="hero-title">
            {t("hero.title_a")}{" "}
            <em className="not-italic text-brand-terracotta font-serif">
              {t("hero.title_em")}
            </em>{" "}
            <br className="hidden md:block" />
            <span className="italic font-light">{t("hero.title_b")}</span>
          </h1>
          <p className="body-lg mt-7 max-w-xl reveal" data-delay="2" data-testid="hero-subtitle">
            {t("hero.sub")}
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3 reveal" data-delay="3">
            <a href="#lead" data-testid="hero-primary-cta" className="btn-primary">
              {t("hero.cta_primary")} <ArrowRight size={16} strokeWidth={2} />
            </a>
            <a href="#pricing" data-testid="hero-secondary-cta" className="btn-ghost">
              {t("hero.cta_secondary")}
            </a>
          </div>

          {/* Stats row */}
          <dl className="mt-12 grid grid-cols-3 gap-6 md:gap-10 max-w-xl reveal" data-delay="4" data-testid="hero-stats">
            {[
              { v: t("hero.stat1_v"), l: t("hero.stat1_l") },
              { v: t("hero.stat2_v"), l: t("hero.stat2_l") },
              { v: t("hero.stat3_v"), l: t("hero.stat3_l") },
            ].map((s, i) => (
              <div key={i} className="border-l border-brand-line pl-4">
                <dt className="font-serif text-3xl md:text-4xl tracking-tight text-brand-forest">
                  {s.v}
                </dt>
                <dd className="text-xs md:text-sm text-brand-mute mt-1 leading-snug">
                  {s.l}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="lg:col-span-5">
          <div className="relative reveal" data-delay="2">
            <div className="relative rounded-[28px] overflow-hidden border border-brand-line shadow-[0_50px_120px_-60px_rgba(26,55,45,0.45)]">
              <img
                src={HERO_IMAGE}
                alt="Warm, elegant café interior with wooden tables and natural light"
                className="w-full h-[520px] md:h-[620px] object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/30 via-transparent to-transparent" />
            </div>
            {/* Floating card */}
            <div
              className="absolute -bottom-8 -left-6 md:-left-10 max-w-[260px] bg-white border border-brand-line rounded-2xl p-5 shadow-[0_30px_60px_-30px_rgba(26,55,45,0.35)]"
              data-testid="hero-floating-card"
            >
              <div className="flex items-center gap-2">
                <span className="inline-block h-2 w-2 rounded-full bg-brand-terracotta" />
                <span className="text-xs uppercase tracking-[0.18em] text-brand-mute">
                  Tonight · 19:30
                </span>
              </div>
              <p className="font-serif text-xl text-brand-ink leading-snug mt-2">
                "Table for 4 booked — direct, no fees."
              </p>
              <p className="text-xs text-brand-mute mt-2">
                Reservation via your own site.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
